/**
 * QMS Approval Panel
 * Panel for Quality Director (QD) approval actions
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Edit, Clock, Send } from 'lucide-react';
import { format } from 'date-fns';
import { useQdApproval, useSubmitForApproval } from '../../hooks/useQmsTicket';
import { useQmsPermissions } from '../../hooks/useQmsPermissions';
import type { QmsTicket, QmsDepartment, QmsPriority, QmsUser } from '../../types/qms.types';
import { isPopulatedUser, extractId } from '../../types/qms.types';
import { QmsUserSelect } from '../shared/QmsUserSelect';

interface QmsApprovalPanelProps {
  ticket: QmsTicket;
  onApprovalComplete: () => void;
}

export function QmsApprovalPanel({ ticket, onApprovalComplete }: QmsApprovalPanelProps) {
  const { t } = useTranslation('qms');
  const { canActAsQD, canAssignTickets } = useQmsPermissions();
  const qdApprovalMutation = useQdApproval();
  const submitForApprovalMutation = useSubmitForApproval();

  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [action, setAction] = useState<'approve' | 'reject' | 'request_changes'>('approve');
  const [notes, setNotes] = useState('');
  const [department, setDepartment] = useState<QmsDepartment | ''>(ticket.department || '');
  const [priority, setPriority] = useState<QmsPriority>(ticket.priority || 3);
  const [assignedUser, setAssignedUser] = useState<QmsUser | undefined>(
    isPopulatedUser(ticket.assigned_to) ? ticket.assigned_to : undefined
  );
  const [showReassignment, setShowReassignment] = useState(false);
  const [requiresCeoReview, setRequiresCeoReview] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const isPending = ticket.qd_approval_status === 'pending';
  const isApproved = ticket.qd_approval_status === 'approved';
  const isRejected = ticket.qd_approval_status === 'rejected';
  const changesRequested = ticket.qd_approval_status === 'changes_requested';

  // Reset department/priority/assignee when ticket changes
  const resetReassignmentState = () => {
    setDepartment(ticket.department || '');
    setPriority(ticket.priority || 3);
    setAssignedUser(isPopulatedUser(ticket.assigned_to) ? ticket.assigned_to : undefined);
    setShowReassignment(false);
  };

  const handleSubmit = async () => {
    setActionError(null);

    // Determine if assigned_to changed
    const currentAssignedId = extractId(ticket.assigned_to);
    const newAssignedId = assignedUser?._id;
    const assignedToChanged = showReassignment && newAssignedId !== currentAssignedId;

    try {
      await qdApprovalMutation.mutateAsync({
        ticketId: ticket._id!,
        payload: {
          action,
          notes,
          ...(showReassignment && department ? { department } : {}),
          ...(showReassignment && priority !== ticket.priority ? { priority } : {}),
          // Send null explicitly to clear assignment, or the new user ID to assign
          ...(assignedToChanged ? { assigned_to: newAssignedId || null } : {}),
          // Only include requires_ceo_review when approving and explicitly requested
          ...(action === 'approve' && requiresCeoReview ? { requires_ceo_review: true } : {}),
        },
      });
      setShowApprovalForm(false);
      setNotes('');
      setRequiresCeoReview(false);
      resetReassignmentState();
      onApprovalComplete();
    } catch (error: any) {
      console.error('Failed to submit QD approval:', error);
      setActionError(error?.message || t('errors.unexpectedError'));
    }
  };

  const handleResubmit = async () => {
    setActionError(null);

    try {
      await submitForApprovalMutation.mutateAsync(ticket._id!);
      onApprovalComplete();
    } catch (error: any) {
      console.error('Failed to resubmit for approval:', error);
      setActionError(error?.message || t('errors.unexpectedError'));
    }
  };

  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('approval.title')}</h3>

      {/* Error Display */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      {/* Current Status */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3">
          {/* Status Icon */}
          {isPending && (
            <>
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium text-gray-900">{t('approval.pendingTitle')}</p>
                <p className="text-sm text-gray-600">{t('approval.pendingDescription')}</p>
              </div>
            </>
          )}

          {isApproved && (
            <>
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">{t('approval.approvedTitle')}</p>
                {isPopulatedUser(ticket.qd_approved_by) && (
                  <p className="text-sm text-gray-600">
                    {ticket.qd_approved_by.firstname} {ticket.qd_approved_by.lastname}
                    {ticket.qd_approved_at && (
                      <> • {format(new Date(ticket.qd_approved_at), 'MMM d, yyyy')}</>
                    )}
                  </p>
                )}
              </div>
            </>
          )}

          {isRejected && (
            <>
              <XCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-medium text-gray-900">{t('approval.rejectedTitle')}</p>
                {isPopulatedUser(ticket.qd_approved_by) && (
                  <p className="text-sm text-gray-600">
                    {ticket.qd_approved_by.firstname} {ticket.qd_approved_by.lastname}
                    {ticket.qd_approved_at && (
                      <> • {format(new Date(ticket.qd_approved_at), 'MMM d, yyyy')}</>
                    )}
                  </p>
                )}
              </div>
            </>
          )}

          {changesRequested && (
            <>
              <Edit className="h-5 w-5 text-orange-600" />
              <div>
                <p className="font-medium text-gray-900">{t('approval.changesRequestedTitle')}</p>
                {isPopulatedUser(ticket.qd_approved_by) && (
                  <p className="text-sm text-gray-600">
                    {ticket.qd_approved_by.firstname} {ticket.qd_approved_by.lastname}
                    {ticket.qd_approved_at && (
                      <> • {format(new Date(ticket.qd_approved_at), 'MMM d, yyyy')}</>
                    )}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Approval Notes */}
        {ticket.qd_approval_notes && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-1">{t('approval.notesLabel')}</p>
            <p className="text-sm text-gray-600">{ticket.qd_approval_notes}</p>
          </div>
        )}
      </div>

      {/* Action Buttons (only if user can act as QD and ticket is pending) */}
      {canActAsQD && isPending && !showApprovalForm && (
        <div className="flex gap-2">
          <button
            onClick={() => {
              setAction('approve');
              setRequiresCeoReview(false);
              setShowApprovalForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            {t('approval.actions.approve')}
          </button>

          <button
            onClick={() => {
              setAction('request_changes');
              setRequiresCeoReview(false);
              setShowApprovalForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Edit className="h-4 w-4" />
            {t('approval.actions.requestChanges')}
          </button>

          <button
            onClick={() => {
              setAction('reject');
              setRequiresCeoReview(false);
              setShowApprovalForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <XCircle className="h-4 w-4" />
            {t('approval.actions.reject')}
          </button>
        </div>
      )}

      {/* Resubmit Button (when changes requested) */}
      {changesRequested && (
        <div className="mt-4">
          <button
            onClick={handleResubmit}
            disabled={submitForApprovalMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-atek-blue text-white rounded-lg hover:bg-atek-blue-dark transition-colors disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {submitForApprovalMutation.isPending
              ? t('approval.actions.resubmitting')
              : t('approval.actions.resubmit')}
          </button>
        </div>
      )}

      {/* Approval Form */}
      {showApprovalForm && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {action === 'approve' && t('approval.form.approvalNotes')}
              {action === 'reject' && t('approval.form.rejectionReason')}
              {action === 'request_changes' && t('approval.form.changesRequired')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent"
              placeholder={t('approval.form.placeholder')}
              required={action !== 'approve'}
            />
          </div>

          {/* CEO Review Option (only for approve action) */}
          {action === 'approve' && (
            <div className="border-t pt-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={requiresCeoReview}
                  onChange={(e) => setRequiresCeoReview(e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">{t('approval.form.requiresCeoReview')}</span>
              </label>
              <p className="text-xs text-gray-500 ml-6 -mt-1 mb-3">{t('approval.form.requiresCeoReviewDescription')}</p>
            </div>
          )}

          {/* Reassignment Options (only for approve action) */}
          {action === 'approve' && (
            <div className="border-t pt-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={showReassignment}
                  onChange={(e) => setShowReassignment(e.target.checked)}
                  className="rounded border-gray-300 text-atek-blue focus:ring-atek-blue"
                />
                <span className="text-sm text-gray-700">{t('approval.form.reassign')}</span>
              </label>

              {showReassignment && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Department */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.fields.department')}
                      </label>
                      <select
                        value={department}
                        onChange={(e) => setDepartment(e.target.value as QmsDepartment)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                      >
                        <option value="">{t('form.placeholders.selectDepartment')}</option>
                        <option value="product">{t('departmentsFull.product')}</option>
                        <option value="sales_marketing">{t('departmentsFull.sales_marketing')}</option>
                        <option value="finance">{t('departmentsFull.finance')}</option>
                        <option value="it">{t('departmentsFull.it')}</option>
                        <option value="hr">{t('departmentsFull.hr')}</option>
                        <option value="quality">{t('departmentsFull.quality')}</option>
                        <option value="operations">{t('departmentsFull.operations')}</option>
                      </select>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.fields.priority')}
                      </label>
                      <select
                        value={priority}
                        onChange={(e) => setPriority(parseInt(e.target.value) as QmsPriority)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                      >
                        <option value={1}>{t('priority.1')}</option>
                        <option value={2}>{t('priority.2')}</option>
                        <option value={3}>{t('priority.3')}</option>
                        <option value={4}>{t('priority.4')}</option>
                        <option value={5}>{t('priority.5')}</option>
                      </select>
                    </div>
                  </div>

                  {/* Assigned To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.assignedTo')}
                    </label>
                    <QmsUserSelect
                      value={assignedUser}
                      onChange={setAssignedUser}
                      disabled={!canAssignTickets}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowApprovalForm(false);
                setNotes('');
                setRequiresCeoReview(false);
                resetReassignmentState();
                setActionError(null);
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {t('actions.cancel')}
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={qdApprovalMutation.isPending || (action !== 'approve' && !notes.trim())}
              className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                action === 'approve'
                  ? 'bg-green-600 hover:bg-green-700'
                  : action === 'reject'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              {qdApprovalMutation.isPending ? t('actions.submitting') : t('actions.submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
